import { useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { m } from "@/paraglide/messages";
import { getLocale } from "@/paraglide/runtime";
import { molecularWeight } from "../lib/molecular-weight";

const EXAMPLES = ["C6H12O6", "NaCl", "Ca(OH)2", "CuSO4·5H2O", "C8H10N4O2"];

function fmt(n: number, locale: string, digits = 2): string {
  return n.toLocaleString(locale, { maximumFractionDigits: digits });
}

export function MolecularWeightCalc() {
  const locale = getLocale();
  const [formula, setFormula] = useState("");
  const result = useMemo(
    () => (formula.trim() ? molecularWeight(formula) : null),
    [formula],
  );

  return (
    <div className="sci-card rounded-2xl p-6 sm:p-8">
      <div className="space-y-1.5">
        <Label htmlFor="mw-formula" className="text-muted-foreground">
          {m.mw_input_label()}
        </Label>
        <Input
          id="mw-formula"
          type="text"
          autoComplete="off"
          spellCheck={false}
          placeholder={m.mw_input_placeholder()}
          value={formula}
          onChange={(e) => setFormula(e.target.value)}
          className="border-black/15 bg-white font-mono text-neutral-900 placeholder:text-neutral-500 hover:border-black/25 dark:border-black/15 dark:bg-white dark:text-neutral-900 dark:placeholder:text-neutral-500"
        />
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <span className="text-xs text-muted-foreground">
          {m.mw_examples_label()}:
        </span>
        {EXAMPLES.map((ex) => (
          <button
            key={ex}
            type="button"
            onClick={() => setFormula(ex)}
            className="sci-chip rounded-md px-2 py-1 font-mono text-xs transition-opacity hover:opacity-80"
          >
            {ex}
          </button>
        ))}
      </div>

      <div className="mt-6" aria-live="polite">
        {result == null ? (
          <p className="text-sm text-muted-foreground">{m.mw_hint_empty()}</p>
        ) : result.ok ? (
          <>
            <div
              data-testid="mw-result"
              className="sci-result-zone flex items-baseline justify-between gap-4 rounded-xl px-5 py-4"
            >
              <span className="text-sm font-medium text-muted-foreground">
                {m.mw_result_label()}
              </span>
              <span className="sci-result font-display text-3xl font-bold sm:text-4xl">
                {fmt(result.molarMass, locale)}{" "}
                <span className="text-xl">{m.mw_unit()}</span>
              </span>
            </div>

            <div className="mt-5 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[var(--border-c)] text-left text-xs tracking-wider text-muted-foreground uppercase">
                    <th className="py-2 pr-4 font-semibold">
                      {m.mw_col_element()}
                    </th>
                    <th className="py-2 pr-4 font-semibold">
                      {m.mw_col_atoms()}
                    </th>
                    <th className="py-2 pr-4 font-semibold">
                      {m.mw_col_atomic()}
                    </th>
                    <th className="py-2 pr-4 font-semibold">
                      {m.mw_col_mass()}
                    </th>
                    <th className="py-2 font-semibold">{m.mw_col_percent()}</th>
                  </tr>
                </thead>
                <tbody className="font-mono">
                  {result.rows.map((r) => (
                    <tr
                      key={r.element}
                      className="border-b border-[var(--border-c)]/50"
                    >
                      <td className="py-2 pr-4 font-semibold text-foreground">
                        {r.element}
                      </td>
                      <td className="py-2 pr-4 text-muted-foreground">
                        {r.count}
                      </td>
                      <td className="py-2 pr-4 text-muted-foreground">
                        {fmt(r.atomicWeight, locale, 3)}
                      </td>
                      <td className="py-2 pr-4 text-foreground">
                        {fmt(r.mass, locale)}
                      </td>
                      <td className="py-2 text-foreground">
                        {fmt(r.percent, locale)}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <p className="text-sm text-red-500 dark:text-red-400" role="alert">
            {result.error}
          </p>
        )}
      </div>
    </div>
  );
}
